/**
 * W91 (2026-06-10) — Reversal-Radar: Das antizyklische Gegenstück zu den
 * Top-Picks.
 *
 * Zeigt Watchlist-Werte im Stage-4-Abwärtstrend, deren Boden-Bewertung läuft —
 * und hebt die hervor, die ERSTE WENDE-ZEICHEN zeigen (Kurs zurück über MA50 +
 * Momentum dreht). Genau dort beginnt für den antizyklischen Trader die
 * Tranchen-Eskalation (Tranche 2/3 des W91-Tranchen-Plans).
 *
 * Datenquelle: localStorage 'cockpit.reversal.cache', gepflegt vom
 * Watchlist-Render (SymbolScoreCell). Einträge altern sichtbar; >7 Tage
 * werden ausgeblendet. Server-seitig lückenlos macht das erst der
 * Score-Cron (W92).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radar } from 'lucide-react';
import {
  loadReversalRadar,
  fetchReversalRadarFromDb,
  type ReversalRadarEntry,
} from '@/lib/reversal-radar';

function ageLabel(iso: string): string {
  const h = (Date.now() - Date.parse(iso)) / 3_600_000;
  if (h < 1) return 'gerade eben';
  if (h < 24) return `vor ${Math.round(h)} Std`;
  return `vor ${Math.round(h / 24)} T`;
}

export function ReversalRadarCard() {
  // W92 — DB-first (Server-Score-Cron schreibt v5_reversal_radar täglich,
  // lückenlos auch ohne Watchlist-Besuch); localStorage als Fallback.
  const localEntries = useMemo(() => loadReversalRadar(), []);
  const [dbEntries, setDbEntries] = useState<ReversalRadarEntry[] | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    void fetchReversalRadarFromDb(ctrl.signal).then((rows) => {
      if (rows && rows.length > 0) setDbEntries(rows);
    });
    return () => ctrl.abort();
  }, []);
  const entries = dbEntries ?? localEntries;

  return (
    <div className="rounded-lg border border-zinc-800/80 bg-zinc-900/40 p-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
          <Radar className="size-4 text-amber-400" />
          Reversal-Radar
        </h2>
        <span className="text-[10px] uppercase tracking-wide text-zinc-500">
          Stage-4-Böden · antizyklisch
        </span>
      </div>
      <p className="text-xs text-zinc-500 mb-3 leading-snug">
        Abgestürzte Quality-Werte in Boden-Beobachtung. 🟢 = erste
        Wende-Zeichen (über MA50, Momentum dreht) — dort beginnt die
        Tranchen-Eskalation. Alle sind noch im Abwärtstrend (Stage 4):
        gestaffelt, nicht ins fallende Messer greifen.
      </p>

      {entries.length === 0 ? (
        <p className="text-xs text-zinc-600 italic">
          Aktuell keine Stage-4-Werte in Boden-Beobachtung. (Das Radar wird
          beim Öffnen der Watchlist aktualisiert.)
        </p>
      ) : (
        <ul className="space-y-1.5">
          {entries.slice(0, 8).map((e) => (
            <li key={e.symbol}>
              <Link
                to={`/symbol/${e.layer}/${encodeURIComponent(e.symbol)}`}
                className="flex items-center gap-3 rounded-md border border-zinc-800/60 bg-zinc-900/60 px-3 py-2 hover:border-zinc-700 focus-ring"
              >
                <span className="text-base" aria-hidden>
                  {e.stabilizing ? '🟢' : '🟠'}
                </span>
                <span className="font-mono text-sm text-zinc-100 w-16 shrink-0">
                  {e.symbol}
                </span>
                <span
                  className={`text-sm font-mono px-1.5 py-0.5 rounded border shrink-0 ${
                    e.stabilizing
                      ? 'bg-emerald-900/30 border-emerald-700/50 text-emerald-200'
                      : 'bg-amber-900/30 border-amber-700/50 text-amber-200'
                  }`}
                >
                  {e.score}
                </span>
                <span className="text-[11px] text-zinc-400 leading-snug line-clamp-2 flex-1">
                  {e.stabilizing
                    ? 'Wende-Zeichen aktiv — Tranche 2/3 prüfen'
                    : 'Boden bildet sich — warten auf Wende'}
                  {/* Diät 14.07. (#39): den identischen Stage-4-Warnsatz aus jedem
                      summary abschneiden — er steht 1× als Legende unter der Liste. */}
                  {e.summary ? ` · ${e.summary.split(' Abwärtstrend (Stage 4)')[0]}` : ''}
                </span>
                <span className="text-[10px] text-zinc-600 shrink-0">
                  {ageLabel(e.updated_at)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
